import React, { useEffect, useState } from 'react';
import {
    ExpandMore,
    ChevronRight,
    Article,
    Security,
    People,
    Public,
    Event,
    CreditCard,
    Warning,
    Description,
    Phone
} from '@mui/icons-material';
import './TermsAndConditions.css';
import NewNavBar from '../../components/NewNavBar/NewNavBar';
import HeaderComponent from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';

const TermsAndConditions = () => {
    const [expandedSections, setExpandedSections] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [])

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const sections = [
        {
            id: 'definitions',
            title: 'Definitions',
            icon: <Description className="section-icon" />,
            content: `
        The following definitions apply throughout these T&C:
        • "User," "You," "Your": Any person or entity visiting, accessing, registering on, or making a Booking through the Platform.
        • "Booking": Any confirmed purchase or reservation for a single activity/experience or a complete tour package.
        • "Supplier" or "Third-Party Supplier": The entity actually providing the service, such as a hotel, airline, local activity operator, visa agency, or transport provider.
        • "Services": All offerings booked through Ghoomo, including but not limited to tours, hotels, flights, experiences, activities, visas, and associated logistics.
        • "Quotation": A formal offer from Ghoomo containing service details, schedule, price, cancellation terms, and payment deadlines.
        • "Platform": Ghoomo's websites, apps, partner pages, social media accounts, and any affiliated channels.
        • "Force Majeure": Events beyond Ghoomo's control, including natural disasters, pandemics, strikes, political unrest, etc.
      `
        },
        {
            id: 'scope',
            title: 'Application & Scope',
            icon: <Public className="section-icon" />,
            content: `
        These Terms & Conditions are split into two parts:
        
        Part A – Direct Experience/Activity Bookings: Covers single experience or activity bookings (e.g., desert safari, museum ticket, guided city tour) with no additional add-ons.
        
        Part B – Complete Tour Packages: Covers multi-service packages (e.g., flights, hotels, multiple experiences, visa facilitation, transfers) as bundled itineraries.
      `
        },
        {
            id: 'legal',
            title: 'Legal Agreement & Booking Contract',
            icon: <Security className="section-icon" />,
            content: `
        Legal Standing:
        • You confirm you are at least 18 years old and have authority to enter a binding contract
        • If booking on behalf of someone else, you are responsible for ensuring they understand and comply with these T&C
        
        Final Quotation as Binding Contract:
        • For Part B bookings, specific terms in the final Quotation become legally binding once payment is made
        • Only services explicitly listed in the final Quotation will be considered part of the confirmed agreement
        • Verbal commitments or previous website content are not binding
        
        Role of Ghoomo:
        • Acts as an aggregator or facilitator; actual Services are provided by independent Suppliers
        • Your contract for the underlying Service is directly with the Supplier
      `
        },
        {
            id: 'partA',
            title: 'Part A: Direct Experience/Activity Bookings',
            icon: <Event className="section-icon" />,
            content: `
        Booking Process:
        • Select activity, date, and number of participants
        • Complete payment to receive Booking Confirmation
        • Check inclusions/exclusions carefully
        
        Supplier's Right to Deny Service:
        • Must comply with age/health restrictions
        • No refunds if denied due to ineligibility, tardiness, or misconduct
        
        Cancellation & Refunds:
        • Governed primarily by Supplier terms
        • Request cancellation via Platform or email
        • Cancellation fees or deadlines may apply
        
        User Responsibilities:
        • Arrive on time or earlier
        • Check local laws and regulations
        • Provide accurate participant details
        • Review all conditions before booking
        
        Assumption of Risk:
        • Some activities carry inherent risks
        • Ghoomo disclaims liability for injuries or losses
      `
        },
        {
            id: 'partB',
            title: 'Part B: Complete Tour Packages',
            icon: <People className="section-icon" />,
            content: `
        Role & Responsibility of Ghoomo:
        • Acts as facilitator between You and multiple Suppliers
        • Does not own or operate airlines, hotels, transport vehicles, or attractions
        • Not responsible for operational decisions of third parties
        
        Booking Conditions & User Obligations:
        • Provide accurate names, passports, ages, contact info
        • Follow specified payment schedule
        • Review final itinerary and documents
        • Comply with local laws and visa regulations
      `
        },
        {
            id: 'flights',
            title: 'Flights Policy',
            icon: <Public className="section-icon" />,
            content: `
        Airline Terms:
        • Tickets subject to each airline's terms and conditions
        • Airlines may reschedule, reroute, or cancel without notice
        • Ghoomo not liable for airline changes
        
        Flight Cancellations & Refunds:
        • Refunds follow airline's fare rules
        • Must notify Ghoomo for refunds or amendments
        • Processing depends on airline refund receipt
        
        Transit Visas & Boarding Denial:
        • User responsible for obtaining transit visas
        • Not liable for boarding denial due to incomplete documents
        
        Web Check-In & Seat Selection:
        • Many airlines require web check-in
        • Not responsible for seat/meal preferences or missed flights
      `
        },
        {
            id: 'hotels',
            title: 'Hotel & Accommodation Policy',
            icon: <People className="section-icon" />,
            content: `
        No Ownership/Operation:
        • Does not own or manage hotels
        • Descriptions provided by respective properties
        • Will arrange similar alternative if unavailable
        
        Check-In Requirements:
        • Hotels set their own policies and timings
        • No refund if denied for failing to meet hotel policy
        • Security deposits handled directly with hotel
        
        Mandatory Charges & Local Taxes:
        • Hotels may levy additional fees
        • Not included in package price
        • Must be settled directly at property
        
        Early Check-In:
        • At hotel's discretion based on availability
        • May incur extra charges
        • Guaranteed early check-in requires previous night booking
      `
        },
        {
            id: 'visa',
            title: 'Visa Policy',
            icon: <Description className="section-icon" />,
            content: `
        Facilitation Only:
        • Facilitates applications through third-party vendors
        • Final issuance/rejection at embassy's discretion
        • Disclaims responsibility for rejections due to incomplete docs
        
        Processing Times & Fees:
        • Processing times are estimates only
        • Visa fees non-refundable even if refused
        
        Travel Impact:
        • Standard cancellation terms apply if visa denied/delayed
        • Not liable for extra costs from denial
        
        Embassy Interviews:
        • May be required for some visas
        • User bears all interview-related costs
        
        Passport Submission:
        • Uses third-party courier agencies
        • Takes precautions but not liable for loss/damage
      `
        },
        {
            id: 'transfers',
            title: 'Transfers & Transport',
            icon: <Public className="section-icon" />,
            content: `
        Group vs. Private Transfers:
        • Group: Shared with multiple stops and passengers
        • Private: Exclusively for your party
        • Be at pickup location 10 mins early
        
        Luggage & Unscheduled Stops:
        • 1 piece + 1 carry-on per person included
        • Additional luggage may require extra fees
        • Drivers may refuse unscheduled stops
        
        Vehicle Breakdown & Delays:
        • Will coordinate alternative vehicle
        • Not liable for missed sightseeing or extra costs
        
        Behavior & Service Denial:
        • Must behave respectfully
        • Service can be denied for misconduct
        
        Refund Policy:
        • Requires valid proof of claim
        • Written acknowledgment or documented evidence needed
      `
        },
        {
            id: 'cancellation',
            title: 'Cancellations & Modifications',
            icon: <Warning className="section-icon" />,
            content: `
        Cancellation by User:
        • Quotation outlines cancellation fee schedule
        • Non-refundable if payments not completed by due dates
        
        Cancellation by Ghoomo:
        • Will inform at least 7 days before departure when feasible
        • Option for full refund or alternative arrangement
        
        Refund Process:
        • Processed after receiving refund from Suppliers
        • Returns to original payment source
        • Convenience fees may be deducted
        
        Modification Policy:
        • Subject to availability
        • May incur 10% modification fee or INR 20,000 (whichever lower)
        • Plus any price difference
        
        Special Cases (Fee Waived):
        • Medical emergencies within 60 days
        • Family emergencies within 60 days
        • Valid documentation required
      `
        },
        {
            id: 'liability',
            title: 'Limitation of Liability',
            icon: <Security className="section-icon" />,
            content: `
        Extent of Liability:
        • Total liability capped at booking amount paid
        • Not liable for personal injury, sickness, accidents, or death
        • Not liable for loss of property or additional costs
        
        Force Majeure:
        • Not in breach when events beyond control prevent service
        • May suspend or terminate affected bookings
        • Refunds only to extent suppliers are willing
        
        No Responsibility for Third-Party Content:
        • Not responsible for user-generated content
        • Not responsible for external links or third-party websites
      `
        },
        {
            id: 'general',
            title: 'General Provisions',
            icon: <Description className="section-icon" />,
            content: `
        User Conduct & Content:
        • No unlawful, harassing, or defamatory content
        • May remove content violating T&C
        • Grant irrevocable license for submitted content
        
        Data Collection & Security:
        • Agree to receive marketing communications
        • May share data with third-party vendors for operations
        • Data may be disclosed to law enforcement when required
        
        Indemnification:
        • User agrees to indemnify Ghoomo from claims arising from breach or misuse
        
        Governing Law:
        • Governed by Rajasthan Jurisdiction
        • Courts in Jaipur have exclusive jurisdiction
      `
        }
    ];

    const filteredSections = sections.filter(section =>
        section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        section.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="terms-container">
            <div><HeaderComponent /></div>
            <div><NewNavBar /></div>
            <div className="terms-header">
                <div className="header-content">
                    <div className="header-icon">
                        <Article style={{ fontSize: 48 }} />
                    </div>
                    <div className="header-text">
                        <h1>Terms and Conditions</h1>
                        {/* <p>Last updated: April 16, 2025</p> */}
                        <p className="header-description">
                            These Terms and Conditions set forth the legally binding framework between you and Ghoomo for accessing our platform and booking services.
                        </p>
                    </div>
                </div>
            </div>

            <div className="terms-content">
                {/* <div className="search-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search terms and conditions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div> */}

                <div className="sections-container">
                    {filteredSections.map((section) => (
                        <div key={section.id} className="section-card">
                            <div
                                className="section-header"
                                onClick={() => toggleSection(section.id)}
                            >
                                <div className="section-title">
                                    {section.icon}
                                    <h2>{section.title}</h2>
                                </div>
                                <div className="section-toggle">
                                    {expandedSections[section.id] ?
                                        <ExpandMore style={{ fontSize: 24 }} /> :
                                        <ChevronRight style={{ fontSize: 24 }} />
                                    }
                                </div>
                            </div>

                            {expandedSections[section.id] && (
                                <div className="section-content">
                                    <div className="content-text">
                                        {section.content.split('\n').map((paragraph, index) => {
                                            const trimmedParagraph = paragraph.trim();
                                            if (trimmedParagraph) {
                                                if (trimmedParagraph.startsWith('•')) {
                                                    return (
                                                        <div key={index} className="bullet-point">
                                                            {/* <span className="bullet">•</span> */}
                                                            <span className="bullet-text">{trimmedParagraph.substring(1).trim()}</span>
                                                        </div>
                                                    );
                                                } else {
                                                    return (
                                                        <p key={index}>
                                                            {trimmedParagraph}
                                                        </p>
                                                    );
                                                }
                                            }
                                            return null;
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* <div className="contact-section">
                    <div className="contact-card">
                        <Phone className="contact-icon" />
                        <div className="contact-content">
                            <h3>Need Help?</h3>
                            <p>
                                For any support, clarifications, or disputes, please reach out to Ghoomo's official help page.
                            </p>
                            <div className="contact-buttons">
                                <button className="contact-btn primary">Contact Support</button>
                                <button className="contact-btn secondary">Help Center</button>
                            </div>
                        </div>
                    </div>
                </div> */}

                <div className="important-notice">
                    <Warning className="notice-icon" />
                    <div className="notice-content">
                        <h3>Important Notice</h3>
                        <p>
                            By using Ghoomo's platform, creating an account, or making any booking, you acknowledge that you have read, understood, and agreed to these Terms and Conditions. If you do not agree, please discontinue use immediately.
                        </p>
                    </div>
                </div>

                <div className="acknowledgment-section">
                    <div className="acknowledgment-card">
                        <h3>Acknowledgment</h3>
                        <p>
                            By (1) creating an account on Ghoomo, (2) making any Booking, or (3) using the Platform, you acknowledge you have read, understood, and agree to these Terms and Conditions in full, including all disclaimers, liabilities, cancellations, modifications, visa/flight/hotel policies, and no-cash deposit rules.
                        </p>
                        <div className="copyright">
                            <p>© 2025 Ghoomo Travel Solutions Pvt Ltd, Ghoomo INC, Ghoomo FZCO, and 53 Take Off Private Limited. All Rights Reserved.</p>
                        </div>
                    </div>
                </div>

            </div>
            <div><Footer /></div>
        </div>
    );
};

export default TermsAndConditions;